import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Package, LogIn, Eye, EyeOff, Mail } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../i18n/LanguageContext';

function Login() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const { login } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(formData.username, formData.password);
    setLoading(false);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post('/auth/forgot-password', { email: forgotPasswordEmail });
      if (response?.data?.success === false || response?.data?.email_sent === false) {
        toast.error('Service email indisponible. Reessayez dans quelques minutes.');
        return;
      }
      toast.success(t('auth.forgotPassword') + ' - Email sent');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="compact" />
        </div>

        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-3">
            <div className="bg-gradient-primary p-3 sm:p-4 rounded-2xl shadow-lg">
              <Package className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">InventoTrack</h1>
          <p className="text-sm sm:text-base text-gray-600">{t('auth.login')}</p>
        </div>

        <Card className="card animate-fadeInUp shadow-2xl border-0">
          <CardHeader className="text-center pb-2 px-4 sm:px-6">
            <CardTitle className="text-xl sm:text-2xl font-semibold text-gray-900">{t('auth.login')}</CardTitle>
          </CardHeader>
          
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-sm">{t('auth.username')}</Label>
                <Input id="username" name="username" type="text" required value={formData.username} onChange={handleChange} className="form-input h-10 sm:h-11" data-testid="login-username-input" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">{t('auth.password')}</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={handleChange} className="form-input h-10 sm:h-11 pr-12" data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full btn-modern btn-primary h-10 sm:h-12 text-sm sm:text-base font-semibold" data-testid="login-submit-button">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="loading-spinner w-4 h-4 border-white"></div>
                    <span>{t('common.loading')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn size={18} />
                    <span>{t('auth.loginButton')}</span>
                  </div>
                )}
              </Button>
              
              <div className="text-center">
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-emerald-600 hover:text-emerald-700 underline" data-testid="forgot-password-link">
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </form>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-3 pt-4 px-4 sm:px-6">
            <div className="text-center text-sm text-gray-600">{t('auth.noAccount')}</div>
            <Link to="/register" className="w-full">
              <Button variant="outline" className="w-full btn-modern btn-outline h-10 sm:h-12 text-sm sm:text-base font-semibold" data-testid="login-register-link">
                {t('auth.registerButton')}
              </Button>
            </Link>
            <Link to="/privacy-policy" className="text-xs text-emerald-700 underline" data-testid="privacy-policy-link-login">
              Politique de confidentialite
            </Link>
          </CardFooter>
        </Card>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowForgotPassword(false)}>
          <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="forgot-password-modal">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Mail className="w-5 h-5 text-emerald-600" />
                {t('auth.forgotPassword')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot_email">{t('auth.email')}</Label>
                  <Input id="forgot_email" type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} required data-testid="forgot-password-email-input" />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)} className="flex-1 h-10" data-testid="cancel-forgot-password">
                    {t('form.cancel')}
                  </Button>
                  <Button type="submit" disabled={forgotPasswordLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="submit-forgot-password">
                    {forgotPasswordLoading ? t('common.loading') : t('stats.send')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Login;
